import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { initGTM, initDataLayer } from '../lib/analytics';

interface AnalyticsContextType {
  isInitialized: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  isInitialized: false,
});

interface AnalyticsProviderProps {
  children: ReactNode;
  gtmId?: string;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children, gtmId }) => {
  const [isInitialized, setIsInitialized] = React.useState(false);

  useEffect(() => {
    const gtmContainerId = gtmId || process.env.REACT_APP_GTM_ID;

    if (gtmContainerId) {
      // Initialize GTM
      initGTM(gtmContainerId);
      setIsInitialized(true);
    } else {
      // Initialize data layer even without GTM for development
      initDataLayer();
      setIsInitialized(true);

      if (process.env.NODE_ENV === 'development') {
        console.warn('[Analytics] GTM ID not found. Analytics will be logged to console only.');
      }
    }
  }, [gtmId]);

  return (
    <AnalyticsContext.Provider value={{ isInitialized }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalyticsContext must be used within AnalyticsProvider');
  }
  return context;
};
